import BaseStepHandler from './BaseStepHandler';
import { Text, Shadow } from 'fabric';

/**
 * Handler for multi-text step type
 */
export default class MultiTextStepHandler extends BaseStepHandler {
    // Default settings for text styling - same as TextStepHandler
    static DEFAULT_SETTINGS = {
        // Outline defaults
        outlineWidth: 1,
        outlineColor: '#000000',
        
        // Shadow defaults
        shadowOffsetX: 3,
        shadowOffsetY: 3,
        shadowBlur: 5,
        shadowColor: 'rgba(0,0,0,0.5)'
    };

    /**
     * Create a new MultiTextStepHandler
     */
    constructor(stepId, stepDefinition, canvas, stepManager, side) {
        super(stepId, stepDefinition, canvas, stepManager, side);
        this.textObjects = new Map(); // Store references to text objects by index
    }
    
    /**
     * Initialize multi-text elements
     */
    initialize() {
        super.initialize();
        
        // Create a container for text objects if it doesn't exist
        const containerId = `${this.stepId}_container`;
        let container = this.canvas.getElement(containerId);
        
        if (!container) {
            // Position in center of canvas if no existing container
            const centerX = this.canvas.canvas.width / 2;
            const centerY = this.canvas.canvas.height / 2;
            
            // Store container info for later use
            this.containerPosition = { x: centerX, y: centerY };
        } else {
            // Store container position
            this.containerPosition = { 
                x: container.left || this.canvas.canvas.width / 2, 
                y: container.top || this.canvas.canvas.height / 2 
            };
        }
        
        // Try to get step data from different sources
        let stepData = this.getStepData();
        console.log(`Initializing MultiTextStep ${this.stepId} with data:`, stepData);
        
        // Check if we need to look for element data directly since step data is empty
        if (!stepData || Object.keys(stepData).length === 0) {
            // Try to get data directly from the canvas element
            const elementId = this.definition.elements && this.definition.elements[0];
            if (elementId) {
                const elementData = this.canvas.getElementData(elementId);
                console.log(`Trying to get data directly from element ${elementId}:`, elementData);
                
                if (elementData && elementData.data) {
                    // Use element data instead
                    stepData = elementData.data;
                    console.log(`Found element data for ${elementId}:`, stepData);
                    
                    // Also store this data in step manager so it's available later
                    this.setStepData(stepData, false);
                }
            }
        }
        
        // Now process data if we have it
        if (stepData && stepData.textFields && stepData.textFields.length > 0) {
            this.updateTextFields(stepData.textFields);
        }
        
        return this;
    }
    
    /**
     * Process template when no data exists
     */
    processTemplate() {
        // First try to get step data
        let stepData = this.getStepData();
        
        // If step data is empty, try to get element data
        if (!stepData || !stepData.textFields || stepData.textFields.length === 0) {
            // Try to get data directly from the canvas element
            const elementId = this.definition.elements && this.definition.elements[0];
            if (elementId) {
                const elementData = this.canvas.getElementData(elementId);                
                if (elementData && elementData.data) {
                    // Use element data instead
                    stepData = elementData.data;
                    
                    // Also store this data in step manager so it's available later
                    this.setStepData(stepData, false);
                }
            }
        }
        
        // If we have data now, update with it
        if (stepData && stepData.textFields && stepData.textFields.length > 0) {
            this.updateTextFields(stepData.textFields);
        } else {
            // Otherwise clear any existing text objects
            this.clearAllTexts();
        }
        
        return this;
    }
    
    /**
     * Activate multi-text elements
     */
    activate() {
        super.activate();
        
        // Make all text elements interactive
        this.textObjects.forEach((textObj) => {
            textObj.set({
                selectable: true,
                hasControls: this.definition.allowedActions?.includes('scale') ?? true,
                lockMovementX: !(this.definition.allowedActions?.includes('move') ?? false),
                lockMovementY: !(this.definition.allowedActions?.includes('move') ?? false),
                cornerStyle: 'circle',
                transparentCorners: false,
                cornerColor: '#0d6efd',
                cornerSize: 50,
                borderScaleFactor:10,
                padding: 10
            });
        });
        
        this.canvas.canvas.requestRenderAll();
        
        return this;
    }
    
    /**
     * Deactivate multi-text elements
     */
    deactivate() {
        super.deactivate();
        
        // Make all text elements non-interactive
        this.textObjects.forEach((textObj) => {
            textObj.set({
                selectable: false,
                hasControls: false,
                lockMovementX: true,
                lockMovementY: true
            });
        });
        
        this.canvas.canvas.requestRenderAll();
        
        return this;
    }
    
    /**
     * Update multi-text elements with step data
     * 
     * @param {object} stepData - Step data
     */
    update(stepData) {
        super.update(stepData);
        
        // Check if we have text fields
        if (!stepData.textFields || !Array.isArray(stepData.textFields)) {
            // Clear all texts
            this.clearAllTexts();
            return this;
        }
        
        // Update text fields
        this.updateTextFields(stepData.textFields);
        
        // Render
        this.canvas.canvas.requestRenderAll();
        
        return this;
    }
    
    /**
     * Clear all text objects
     */
    clearAllTexts() {
        // Remove all text objects from canvas
        this.textObjects.forEach((textObj) => {
            this.canvas.canvas.remove(textObj);
            
            // Also remove from canvas reference map
            const textId = textObj.name;
            if (textId) {
                this.canvas.elementRefs.delete(textId);
            }
        });
        
        // Clear text objects map
        this.textObjects.clear();
        
        // Force re-render
        this.canvas.canvas.requestRenderAll();
    }
    
    /**
     * Update text fields
     * 
     * @param {Array} textFields - Text field data
     */
    updateTextFields(textFields) {
        // Track which texts we've updated
        const updatedIndices = new Set();
        
        // Calculate initial positions if needed
        const centerX = this.containerPosition?.x || this.canvas.canvas.width / 2;
        const centerY = this.containerPosition?.y || this.canvas.canvas.height / 2;
        const spacing = 30; // Vertical spacing between text elements
        const startY = centerY - ((textFields.length - 1) * spacing / 2);
        
        // Update or create text objects
        textFields.forEach((field, index) => {
            if (!field.text) return; // Skip empty fields
            
            // Track that we've updated this index
            updatedIndices.add(index);
            
            let textObj = this.textObjects.get(index);
            const textName = `${this.stepId}_text_${index}`;
            
            if (textObj) {
                // Update existing text with all properties
                const textProps = {
                    text: field.text,
                    fontFamily: field.font || 'Arial',
                    fill: field.color || '#000000',
                    // Use saved position if available, otherwise use default
                    left: field.left !== undefined ? field.left : centerX,
                    top: field.top !== undefined ? field.top : (startY + index * spacing)
                };
                
                // Add additional text properties if they exist
                if (field.fontSize !== undefined) textProps.fontSize = field.fontSize;
                if (field.fontWeight !== undefined) textProps.fontWeight = field.fontWeight;
                if (field.fontStyle !== undefined) textProps.fontStyle = field.fontStyle;
                if (field.underline !== undefined) textProps.underline = field.underline;
                if (field.textAlign !== undefined) textProps.textAlign = field.textAlign;
                if (field.angle !== undefined) textProps.angle = field.angle;
                
                // Apply all properties
                textObj.set(textProps);
                
                // Apply scale if available
                if (field.scaleX !== undefined) textObj.set('scaleX', field.scaleX);
                if (field.scaleY !== undefined) textObj.set('scaleY', field.scaleY);
                
                // Apply advanced styling options
                this.applyAdvancedStyling(textObj, field);
                
            } else {
                // Create new text with all properties
                const textProps = {
                    name: textName,
                    fontFamily: field.font || 'Arial',
                    fill: field.color || '#000000',
                    fontSize: field.fontSize || 80,
                    originX: 'center',
                    originY: 'center',
                    left: field.left !== undefined ? field.left : centerX,
                    top: field.top !== undefined ? field.top : (startY + index * spacing),
                    scaleX: field.scaleX !== undefined ? field.scaleX : 1,
                    scaleY: field.scaleY !== undefined ? field.scaleY : 1,
                    selectable: this.isActive,
                    hasControls: this.isActive && (this.definition.allowedActions?.includes('scale') ?? true),
                    lockMovementX: !(this.isActive && (this.definition.allowedActions?.includes('move') ?? false)),
                    lockMovementY: !(this.isActive && (this.definition.allowedActions?.includes('move') ?? false)),
                    cornerStyle: 'circle',
                    transparentCorners: false,
                    cornerColor: '#0d6efd',
                    cornerSize: 50,
                    borderScaleFactor:10,
                    padding: 10
                };
                
                // Add additional text properties if they exist
                if (field.fontWeight !== undefined) textProps.fontWeight = field.fontWeight;
                if (field.fontStyle !== undefined) textProps.fontStyle = field.fontStyle;
                if (field.underline !== undefined) textProps.underline = field.underline;
                if (field.textAlign !== undefined) textProps.textAlign = field.textAlign;
                if (field.angle !== undefined) textProps.angle = field.angle;
                
                // Create text object
                textObj = new Text(field.text, textProps);
                
                // Apply advanced styling options
                this.applyAdvancedStyling(textObj, field);
                
                // Add modification listener to update positions in step data
                textObj.on('modified', () => this.handleTextModified(index, textObj));
                
                // Add to canvas
                this.canvas.canvas.add(textObj);
                
                // Store references
                this.textObjects.set(index, textObj);
                this.canvas.elementRefs.set(textName, textObj);
            }
        });
        
        // Remove any text objects that weren't updated
        this.textObjects.forEach((textObj, index) => {
            if (!updatedIndices.has(index)) {
                this.canvas.canvas.remove(textObj);
                
                const textName = `${this.stepId}_text_${index}`;
                this.canvas.elementRefs.delete(textName);
                this.textObjects.delete(index);
            }
        });
    }
    
    /**
     * Apply advanced styling options (outline and shadow) to a text object
     * 
     * @param {Text} textObj - Fabric text object
     * @param {object} field - Field data with styling options
     */
    applyAdvancedStyling(textObj, field) {
        // Apply outline properties
        if (field.outlineEnabled) {
            textObj.set({
                stroke: field.outlineColor || MultiTextStepHandler.DEFAULT_SETTINGS.outlineColor,
                strokeWidth: field.outlineWidth !== undefined ? field.outlineWidth : MultiTextStepHandler.DEFAULT_SETTINGS.outlineWidth
            });
        } else {
            textObj.set({
                stroke: null,
                strokeWidth: 0
            });
        }
        
        // Apply shadow properties
        if (field.shadowEnabled) {
            const shadowColor = field.shadowColor || MultiTextStepHandler.DEFAULT_SETTINGS.shadowColor;
            const offsetX = field.shadowOffsetX !== undefined ? 
                            field.shadowOffsetX : 
                            MultiTextStepHandler.DEFAULT_SETTINGS.shadowOffsetX;
            const offsetY = field.shadowOffsetY !== undefined ? 
                            field.shadowOffsetY : 
                            MultiTextStepHandler.DEFAULT_SETTINGS.shadowOffsetY;
            const blur = field.shadowBlur !== undefined ? 
                        field.shadowBlur : 
                        MultiTextStepHandler.DEFAULT_SETTINGS.shadowBlur;
            
            textObj.set('shadow', new Shadow({
                color: shadowColor,
                offsetX: offsetX,
                offsetY: offsetY,
                blur: blur
            }));
        } else {
            textObj.set('shadow', null);
        }
    }
    
    /**
     * Handle text object modification
     * 
     * @param {number} index - Text field index
     * @param {object} textObj - Fabric text object
     */
    handleTextModified(index, textObj) {
        // Update step data with new position/scale
        const stepData = this.getStepData() || {};
        
        if (!stepData.textFields) {
            stepData.textFields = [];
        }
        
        if (!stepData.textFields[index]) {
            stepData.textFields[index] = {
                text: textObj.text,
                font: textObj.fontFamily,
                color: textObj.fill
            };
        }
        
        // Update all properties
        const field = stepData.textFields[index];
        
        // Update basic properties
        field.left = textObj.left;
        field.top = textObj.top;
        field.scaleX = textObj.scaleX;
        field.scaleY = textObj.scaleY;
        
        // Update additional text properties
        field.fontSize = textObj.fontSize;
        field.fontWeight = textObj.fontWeight;
        field.fontStyle = textObj.fontStyle;
        field.underline = textObj.underline;
        field.textAlign = textObj.textAlign;
        field.angle = textObj.angle;
        
        // Save without triggering update (would cause loop)
        this.setStepData(stepData, false);
    }
    
    /**
     * Get available fonts for the text editor
     * @returns {Array} Array of font objects with value and label
     */
    getAvailableFonts() {
        return [
            { value: 'Arial', label: 'Arial' },
            { value: 'Times New Roman', label: 'Times New Roman' },
            { value: 'Helvetica', label: 'Helvetica' },
            { value: 'AgencyFB-Bold', label: 'Agency FB Bold' },
            { value: 'AgencyFB-Reg', label: 'Agency FB Regular' },
            { value: 'Anton', label: 'Anton' },
            { value: 'ArchivoNarrow-Regular', label: 'Archivo Narrow Regular' },
            { value: 'EdwardianScriptITC', label: 'Edwardian Script ITC' },
            { value: 'Freshman', label: 'Freshman' },
            { value: 'GOODBRUSH', label: 'GOODBRUSH' },
            { value: 'Impact', label: 'Impact' },
            { value: 'KristenITC-Regular', label: 'Kristen ITC Regular' },
            { value: 'MyriadPro-BoldCondIt', label: 'Myriad Pro Bold Cond It' },
            { value: 'MyriadPro-Regular', label: 'Myriad Pro Regular' },
            { value: 'OrdinaryGuyDEMO', label: 'Ordinary Guy DEMO' },
            { value: 'OswaldStencil-Bold', label: 'Oswald Stencil Bold' }
        ];
    }
    
    /**
     * Get font select options HTML with styled options
     * @param {string} selectedFont - Currently selected font
     * @returns {string} HTML for select options
     */
    getFontOptionsHtml(selectedFont) {
        const fonts = this.getAvailableFonts();
        
        return fonts.map(font => `
            <option 
                value="${font.value}" 
                ${selectedFont === font.value ? 'selected' : ''} 
                style="font-family: '${font.value}', Arial, sans-serif;">
                ${font.label}
            </option>
        `).join('');
    }
    
    /**
     * Extract opacity value from rgba color string
     * @param {string} color - Color value
     * @returns {number} Opacity value (0-1)
     */
    extractOpacity(color) {
        if (!color || typeof color !== 'string') return 0.5;
        
        if (color.startsWith('rgba')) {
            const match = color.match(/rgba\(.*,\s*([0-9.]+)\)/);
            if (match && match[1]) {
                return parseFloat(match[1]);
            }
        }
        return 0.5; // Default opacity
    }
    
    /**
     * Create rgba color from hex color and opacity
     * @param {string} hexColor - Hex color value
     * @param {number} opacity - Opacity value (0-1)
     * @returns {string} RGBA color string
     */
    createRgbaColor(hexColor, opacity) {
        // Convert hex to rgb
        let hex = hexColor.replace('#', '');
        let r = parseInt(hex.substring(0, 2), 16);
        let g = parseInt(hex.substring(2, 4), 16);
        let b = parseInt(hex.substring(4, 6), 16);
        
        // Return rgba
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    
    /**
     * Ensure field has all required properties with default values
     * @param {object} field - Field object to validate
     * @returns {object} Field with all properties
     */
    ensureFieldDefaults(field) {
        return {
            text: field.text || '',
            font: field.font || 'Arial',
            color: field.color || '#000000',
            fontSize: field.fontSize || 80,
            fontWeight: field.fontWeight || 'normal',
            fontStyle: field.fontStyle || 'normal',
            underline: field.underline || false,
            textAlign: field.textAlign || 'center',
            
            // Advanced options with defaults
            advancedExpanded: field.advancedExpanded || false,
            outlineEnabled: field.outlineEnabled || false,
            outlineColor: field.outlineColor || MultiTextStepHandler.DEFAULT_SETTINGS.outlineColor,
            outlineWidth: field.outlineWidth !== undefined ? field.outlineWidth : MultiTextStepHandler.DEFAULT_SETTINGS.outlineWidth,
            shadowEnabled: field.shadowEnabled || false,
            shadowColor: field.shadowColor || MultiTextStepHandler.DEFAULT_SETTINGS.shadowColor,
            shadowOffsetX: field.shadowOffsetX !== undefined ? field.shadowOffsetX : MultiTextStepHandler.DEFAULT_SETTINGS.shadowOffsetX,
            shadowOffsetY: field.shadowOffsetY !== undefined ? field.shadowOffsetY : MultiTextStepHandler.DEFAULT_SETTINGS.shadowOffsetY,
            shadowBlur: field.shadowBlur !== undefined ? field.shadowBlur : MultiTextStepHandler.DEFAULT_SETTINGS.shadowBlur,
            
            // Position properties
            left: field.left,
            top: field.top,
            scaleX: field.scaleX,
            scaleY: field.scaleY,
            angle: field.angle
        };
    }
    
    /**
     * Render panel content
     */
    renderPanelContent() {
        const stepData = this.getStepData();
        const textFields = stepData.textFields || [];
        const maxFields = this.definition.maxFields || 5;
        const canAddMore = textFields.length < maxFields;
        
        return `
            <div class="stc-multi-text-field">
                <div class="stc-multi-text-fields">
                    ${textFields.map((field, index) => {
                        // Ensure field has all required properties
                        const safeField = this.ensureFieldDefaults(field);
                        
                        return `
                        <div class="stc-multi-text-item" data-index="${index}">
                            <div class="stc-text-field">
                                <button type="button" class="stc-remove-text-field" data-index="${index}">
                                    <span class="dashicons dashicons-trash"></span>
                                </button>
                                <input type="text" 
                                    class="stc-input stc-multi-text-input" 
                                    data-index="${index}"
                                    placeholder="Enter text"
                                    value="${safeField.text}">
                                <div class="stc-text-controls">
                                    <input type="color" 
                                        class="stc-color-picker"
                                        data-index="${index}"
                                        value="${safeField.color}">
                                    <div class="stc-font-size-control">
                                        <input type="number" 
                                            class="stc-font-size-input" 
                                            data-index="${index}" 
                                            value="${safeField.fontSize}" 
                                            min="8" 
                                            max="72">
                                        <label>px</label>
                                    </div>
                                    <select class="stc-font-select" data-index="${index}">
                                        ${this.getFontOptionsHtml(safeField.font)}
                                    </select>
                                </div>
                                
                                <!-- Advanced Options Toggle -->
                                <div class="stc-advanced-toggle">
                                    <button type="button" class="stc-toggle-advanced" data-index="${index}">
                                        ${safeField.advancedExpanded ? 'Hide' : 'Show'} Advanced Options
                                        <span class="stc-toggle-icon">${safeField.advancedExpanded ? '▲' : '▼'}</span>
                                    </button>
                                </div>
                                
                                <!-- Advanced Options Panel -->
                                <div class="stc-advanced-options ${safeField.advancedExpanded ? 'is-expanded' : ''}" data-index="${index}">
                                    <!-- Outline Controls -->
                                    <div class="stc-option-section">
                                        <div class="stc-option-header">
                                            <label class="stc-switch">
                                                <input type="checkbox" class="stc-outline-toggle" data-index="${index}" ${safeField.outlineEnabled ? 'checked' : ''}>
                                                <span class="stc-slider"></span>
                                            </label>
                                            <span class="stc-toggle-label">Text Outline</span>
                                        </div>
                                        
                                        <div class="stc-option-controls ${safeField.outlineEnabled ? 'is-enabled' : ''}">
                                            <div class="stc-outline-controls">
                                                <div class="stc-control-row">
                                                    <div class="stc-control-column">
                                                        <label>Color</label>
                                                        <input type="color" class="stc-outline-color" data-index="${index}" value="${safeField.outlineColor}">
                                                    </div>
                                                    <div class="stc-control-column">
                                                        <label>Width</label>
                                                        <div class="stc-slider-container">
                                                            <input type="range" 
                                                                class="stc-outline-width" 
                                                                data-index="${index}"
                                                                min="0.5" 
                                                                max="5" 
                                                                step="0.5" 
                                                                value="${safeField.outlineWidth}">
                                                            <span class="stc-slider-value">${safeField.outlineWidth}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <!-- Shadow Controls -->
                                    <div class="stc-option-section">
                                        <div class="stc-option-header">
                                            <label class="stc-switch">
                                                <input type="checkbox" class="stc-shadow-toggle" data-index="${index}" ${safeField.shadowEnabled ? 'checked' : ''}>
                                                <span class="stc-slider"></span>
                                            </label>
                                            <span class="stc-toggle-label">Text Shadow</span>
                                        </div>
                                        
                                        <div class="stc-option-controls ${safeField.shadowEnabled ? 'is-enabled' : ''}">
                                            <div class="stc-shadow-controls">
                                                <div class="stc-control-row">
                                                    <div class="stc-control-column">
                                                        <label>Color</label>
                                                        <input type="color" class="stc-shadow-color" data-index="${index}" value="${safeField.shadowColor.startsWith('rgba') ? '#000000' : safeField.shadowColor}">
                                                    </div>
                                                    <div class="stc-control-column">
                                                        <label>Opacity</label>
                                                        <div class="stc-slider-container">
                                                            <input type="range" 
                                                                class="stc-shadow-opacity" 
                                                                data-index="${index}"
                                                                min="0" 
                                                                max="1" 
                                                                step="0.1" 
                                                                value="${this.extractOpacity(safeField.shadowColor)}">
                                                            <span class="stc-slider-value">${this.extractOpacity(safeField.shadowColor)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div class="stc-control-row">
                                                    <div class="stc-control-column">
                                                        <label>X Offset</label>
                                                        <div class="stc-slider-container">
                                                            <input type="range" 
                                                                class="stc-shadow-offsetx" 
                                                                data-index="${index}"
                                                                min="-20" 
                                                                max="20" 
                                                                step="1" 
                                                                value="${safeField.shadowOffsetX}">
                                                            <span class="stc-slider-value">${safeField.shadowOffsetX}</span>
                                                        </div>
                                                    </div>
                                                    <div class="stc-control-column">
                                                        <label>Y Offset</label>
                                                        <div class="stc-slider-container">
                                                            <input type="range" 
                                                                class="stc-shadow-offsety" 
                                                                data-index="${index}"
                                                                min="-20" 
                                                                max="20" 
                                                                step="1" 
                                                                value="${safeField.shadowOffsetY}">
                                                            <span class="stc-slider-value">${safeField.shadowOffsetY}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div class="stc-control-row">
                                                    <div class="stc-control-column">
                                                        <label>Blur</label>
                                                        <div class="stc-slider-container">
                                                            <input type="range" 
                                                                class="stc-shadow-blur" 
                                                                data-index="${index}"
                                                                min="0" 
                                                                max="20" 
                                                                step="1" 
                                                                value="${safeField.shadowBlur}">
                                                            <span class="stc-slider-value">${safeField.shadowBlur}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        `;
                    }).join('')}
                </div>
                ${canAddMore ? `
                    <button type="button" class="stc-add-text-field">
                        <span class="dashicons dashicons-plus-alt"></span> Add Text Field
                    </button>
                ` : ''}
            </div>
        `;
    }
    
    /**
     * Bind panel events
     */
    bindPanelEvents() {
        if (!this.panelElement) return;
        
        // Get UI elements
        const addButton = this.panelElement.querySelector('.stc-add-text-field');
        const clearButton = this.panelElement.querySelector('.stc-clear-step');
        const textItems = this.panelElement.querySelectorAll('.stc-multi-text-item');
        
        // Bind add button
        if (addButton) {
            this.addEventListener(addButton, 'click', () => {
                this.addTextField();
            });
        }
        
        // Bind clear button
        if (clearButton) {
            this.addEventListener(clearButton, 'click', () => {
                this.clear();
            });
        }
        
        // Bind events for each text field
        textItems.forEach(item => {
            const index = parseInt(item.dataset.index, 10);
            if (isNaN(index)) return;
            
            this.bindTextFieldEvents(item, index);
        });
    }
    
    /**
     * Bind events for a specific text field
     * @param {HTMLElement} item - Text field container element
     * @param {number} index - Field index
     */
    bindTextFieldEvents(item, index) {
        // Basic controls
        const textInput = item.querySelector('.stc-multi-text-input');
        const colorPicker = item.querySelector('.stc-color-picker');
        const fontSelect = item.querySelector('.stc-font-select');
        const fontSizeInput = item.querySelector('.stc-font-size-input');
        const removeButton = item.querySelector('.stc-remove-text-field');
        
        // Advanced options toggle
        const advancedToggle = item.querySelector('.stc-toggle-advanced');
        const advancedPanel = item.querySelector('.stc-advanced-options');
        
        // Outline controls
        const outlineToggle = item.querySelector('.stc-outline-toggle');
        const outlineColor = item.querySelector('.stc-outline-color');
        const outlineWidth = item.querySelector('.stc-outline-width');
        const outlineWidthValue = outlineWidth?.nextElementSibling;
        const outlineControls = item.querySelector('.stc-outline-controls').closest('.stc-option-controls');
        
        // Shadow controls
        const shadowToggle = item.querySelector('.stc-shadow-toggle');
        const shadowColor = item.querySelector('.stc-shadow-color');
        const shadowOpacity = item.querySelector('.stc-shadow-opacity');
        const shadowOpacityValue = shadowOpacity?.nextElementSibling;
        const shadowOffsetX = item.querySelector('.stc-shadow-offsetx');
        const shadowOffsetXValue = shadowOffsetX?.nextElementSibling;
        const shadowOffsetY = item.querySelector('.stc-shadow-offsety');
        const shadowOffsetYValue = shadowOffsetY?.nextElementSibling;
        const shadowBlur = item.querySelector('.stc-shadow-blur');
        const shadowBlurValue = shadowBlur?.nextElementSibling;
        const shadowControls = item.querySelector('.stc-shadow-controls').closest('.stc-option-controls');
        
        // Bind basic controls
        if (textInput) {
            this.addEventListener(textInput, 'input', () => {
                this.updateTextFieldProperty(index, 'text', textInput.value);
            });
        }
        
        if (colorPicker) {
            this.addEventListener(colorPicker, 'input', () => {
                this.updateTextFieldProperty(index, 'color', colorPicker.value);
            });
        }
        
        if (fontSelect) {
            this.addEventListener(fontSelect, 'change', () => {
                this.updateTextFieldProperty(index, 'font', fontSelect.value);
            });
        }
        
        if (fontSizeInput) {
            this.addEventListener(fontSizeInput, 'input', () => {
                this.updateTextFieldProperty(index, 'fontSize', parseInt(fontSizeInput.value, 10));
            });
        }
        
        if (removeButton) {
            this.addEventListener(removeButton, 'click', () => {
                this.removeTextField(index);
            });
        }
        
        // Bind advanced options toggle
        if (advancedToggle) {
            this.addEventListener(advancedToggle, 'click', () => {
                this.toggleAdvancedOptions(index, advancedPanel, advancedToggle);
            });
        }
        
        // Bind outline controls
        if (outlineToggle) {
            this.addEventListener(outlineToggle, 'change', () => {
                this.updateTextFieldProperty(index, 'outlineEnabled', outlineToggle.checked);
                outlineControls.classList.toggle('is-enabled', outlineToggle.checked);
            });
        }
        
        if (outlineColor) {
            this.addEventListener(outlineColor, 'input', () => {
                this.updateTextFieldProperty(index, 'outlineColor', outlineColor.value);
            });
        }
        
        if (outlineWidth) {
            this.addEventListener(outlineWidth, 'input', () => {
                const value = parseFloat(outlineWidth.value);
                this.updateTextFieldProperty(index, 'outlineWidth', value);
                if (outlineWidthValue) outlineWidthValue.textContent = value;
            });
        }
        
        // Bind shadow controls
        if (shadowToggle) {
            this.addEventListener(shadowToggle, 'change', () => {
                this.updateTextFieldProperty(index, 'shadowEnabled', shadowToggle.checked);
                shadowControls.classList.toggle('is-enabled', shadowToggle.checked);
            });
        }
        
        if (shadowColor) {
            this.addEventListener(shadowColor, 'input', () => {
                const opacity = shadowOpacity ? parseFloat(shadowOpacity.value) : 0.5;
                const rgbaColor = this.createRgbaColor(shadowColor.value, opacity);
                this.updateTextFieldProperty(index, 'shadowColor', rgbaColor);
            });
        }
        
        if (shadowOpacity) {
            this.addEventListener(shadowOpacity, 'input', () => {
                const hexColor = shadowColor ? shadowColor.value : '#000000';
                const opacity = parseFloat(shadowOpacity.value);
                const rgbaColor = this.createRgbaColor(hexColor, opacity);
                this.updateTextFieldProperty(index, 'shadowColor', rgbaColor);
                if (shadowOpacityValue) shadowOpacityValue.textContent = opacity.toFixed(1);
            });
        }
        
        if (shadowOffsetX) {
            this.addEventListener(shadowOffsetX, 'input', () => {
                const value = parseInt(shadowOffsetX.value);
                this.updateTextFieldProperty(index, 'shadowOffsetX', value);
                if (shadowOffsetXValue) shadowOffsetXValue.textContent = value;
            });
        }
        
        if (shadowOffsetY) {
            this.addEventListener(shadowOffsetY, 'input', () => {
                const value = parseInt(shadowOffsetY.value);
                this.updateTextFieldProperty(index, 'shadowOffsetY', value);
                if (shadowOffsetYValue) shadowOffsetYValue.textContent = value;
            });
        }
        
        if (shadowBlur) {
            this.addEventListener(shadowBlur, 'input', () => {
                const value = parseInt(shadowBlur.value);
                this.updateTextFieldProperty(index, 'shadowBlur', value);
                if (shadowBlurValue) shadowBlurValue.textContent = value;
            });
        }
    }
    
    /**
     * Toggle advanced options for a specific text field
     * @param {number} index - Field index
     * @param {HTMLElement} panel - Advanced options panel
     * @param {HTMLElement} toggle - Toggle button
     */
    toggleAdvancedOptions(index, panel, toggle) {
        const stepData = this.getStepData() || {};
        if (!stepData.textFields || !stepData.textFields[index]) return;
        
        const isExpanded = !stepData.textFields[index].advancedExpanded;
        stepData.textFields[index].advancedExpanded = isExpanded;
        
        // Update UI
        panel.classList.toggle('is-expanded', isExpanded);
        const icon = toggle.querySelector('.stc-toggle-icon');
        const textNode = toggle.firstChild;
        
        if (icon) icon.textContent = isExpanded ? '▲' : '▼';
        if (textNode) textNode.textContent = isExpanded ? 'Hide ' : 'Show ';
        
        // Save without triggering full update
        this.setStepData(stepData, false);
    }
    
    /**
     * Add a new text field
     */
    addTextField() {
        const stepData = this.getStepData() || {};
        if (!stepData.textFields) {
            stepData.textFields = [];
        }
        
        // Check if we're at the limit
        if (stepData.textFields.length >= (this.definition.maxFields || 5)) {
            return;
        }
        
        // Add new empty field with all default properties
        stepData.textFields.push(this.ensureFieldDefaults({}));
        
        // Update state and UI
        this.setStepData(stepData);
        this.updatePanel();
    }
    
    /**
     * Remove a text field
     * 
     * @param {number} index - Field index
     */
    removeTextField(index) {
        const stepData = this.getStepData() || {};
        if (!stepData.textFields || !stepData.textFields[index]) {
            return;
        }
        
        // Remove the field from data
        stepData.textFields.splice(index, 1);
        
        // Get the text object for this index and remove it
        const textObj = this.textObjects.get(index);
        if (textObj) {
            this.canvas.canvas.remove(textObj);
            
            const textName = `${this.stepId}_text_${index}`;
            this.canvas.elementRefs.delete(textName);
            this.textObjects.delete(index);
        }
        
        // Remove from our map and shift other indices
        const newTextObjects = new Map();
        this.textObjects.forEach((obj, idx) => {
            if (idx < index) {
                newTextObjects.set(idx, obj);
            } else if (idx > index) {
                // Update name to reflect new index
                const newIndex = idx - 1;
                const newName = `${this.stepId}_text_${newIndex}`;
                
                // Update in canvas references
                this.canvas.elementRefs.delete(obj.name);
                obj.set('name', newName);
                this.canvas.elementRefs.set(newName, obj);
                
                // Update in our map
                newTextObjects.set(newIndex, obj);
            }
        });
        
        this.textObjects = newTextObjects;
        
        // Update state and UI
        this.setStepData(stepData);
        this.updatePanel();
        
        // Rerender text fields from data to ensure positions are correct
        this.updateTextFields(stepData.textFields);
    }
    
    /**
     * Update a text field property
     * 
     * @param {number} index - Field index
     * @param {string} property - Property name
     * @param {any} value - Property value
     */
    updateTextFieldProperty(index, property, value) {
        const stepData = this.getStepData() || {};
        if (!stepData.textFields) {
            stepData.textFields = [];
        }
        
        // Ensure field exists with all properties
        if (!stepData.textFields[index]) {
            stepData.textFields[index] = this.ensureFieldDefaults({});
        }
        
        // Update property
        stepData.textFields[index][property] = value;
        
        // Update state - this will trigger updateTextFields which applies styling
        this.setStepData(stepData);
    }
    
    /**
     * Handle side switching to hide elements
     */
    handleSideChange(isCurrentSide) {
        if (isCurrentSide) {
            // This handler's side is now active
            this.processTemplate();
        } else {
            // This handler's side is now inactive - hide elements
            this.hideTextFields();
        }
        
        return this;
    }
    
    /**
     * Hide all text fields when switching sides
     */
    hideTextFields() {
        this.textObjects.forEach((textObj) => {
            textObj.set('visible', false);
        });
        
        this.canvas.canvas.requestRenderAll();
    }
    
    /**
     * Step-specific reset logic
     */
    onReset() {
        // For multi-text, we need to remove all text objects and clear data
        this.clearAllTexts();
    }
    
    /**
     * Get export data for multi-text step
     * 
     * @returns {object} Export data
     */
    getStepExportData() {
        // Get base export data
        const exportData = super.getStepExportData();
        const stepData = this.getStepData();
        
        // Add multi-text specific export data
        exportData.multiTextContent = {
            fields: [],
            maxFields: this.definition.maxFields || 5
        };
        
        // Add each text field's data
        if (stepData.textFields && Array.isArray(stepData.textFields)) {
            stepData.textFields.forEach((field, index) => {
                if (!field.text) return; // Skip empty fields
                
                // Get position and styling data from text object if available
                const textObj = this.textObjects.get(index);
                const position = textObj ? {
                    left: textObj.left,
                    top: textObj.top,
                    scaleX: textObj.scaleX,
                    scaleY: textObj.scaleY,
                    width: textObj.width,
                    height: textObj.height,
                    angle: textObj.angle || 0
                } : {
                    left: field.left,
                    top: field.top,
                    scaleX: field.scaleX || 1,
                    scaleY: field.scaleY || 1,
                    angle: field.angle || 0
                };
                
                // Add to fields array with all properties
                exportData.multiTextContent.fields.push({
                    index,
                    text: field.text,
                    font: field.font || 'Arial',
                    color: field.color || '#000000',
                    fontSize: field.fontSize || 80,
                    fontWeight: field.fontWeight || 'normal',
                    fontStyle: field.fontStyle || 'normal',
                    underline: field.underline || false,
                    textAlign: field.textAlign || 'center',
                    position,
                    // Include advanced styling options
                    styling: {
                        outlineEnabled: field.outlineEnabled || false,
                        outlineColor: field.outlineColor || MultiTextStepHandler.DEFAULT_SETTINGS.outlineColor,
                        outlineWidth: field.outlineWidth !== undefined ? field.outlineWidth : MultiTextStepHandler.DEFAULT_SETTINGS.outlineWidth,
                        shadowEnabled: field.shadowEnabled || false,
                        shadowColor: field.shadowColor || MultiTextStepHandler.DEFAULT_SETTINGS.shadowColor,
                        shadowOffsetX: field.shadowOffsetX !== undefined ? field.shadowOffsetX : MultiTextStepHandler.DEFAULT_SETTINGS.shadowOffsetX,
                        shadowOffsetY: field.shadowOffsetY !== undefined ? field.shadowOffsetY : MultiTextStepHandler.DEFAULT_SETTINGS.shadowOffsetY,
                        shadowBlur: field.shadowBlur !== undefined ? field.shadowBlur : MultiTextStepHandler.DEFAULT_SETTINGS.shadowBlur
                    }
                });
            });
        }
        
        return exportData;
    }
    
    /**
     * Clean up resources
     */
    dispose() {
        // Clear all text objects before disposal
        this.clearAllTexts();
        
        // Call parent dispose
        super.dispose();
    }

    /**
     * Check if step is complete
     */
    isComplete() {
        const stepData = this.getStepData();
        let haveTexts = stepData.textFields && Array.isArray(stepData.textFields) && stepData.textFields.length > 0;
        return haveTexts;
    }

    show() {
        const stepData = this.getStepData();
        
        // Check if stepData and textFields exist
        if (!stepData || !stepData.textFields || !Array.isArray(stepData.textFields)) {
            return;
        }
        
        stepData.textFields.forEach((field, index) => {
            const obj = this.textObjects.get(index);
            // Check if the object exists before calling set
            if (obj) {
                obj.set('visible', true);
            }
        });

        this.canvas.canvas.requestRenderAll();
    }

    hide() {
        const stepData = this.getStepData();
        
        // Check if stepData and textFields exist
        if (!stepData || !stepData.textFields || !Array.isArray(stepData.textFields)) {
            return;
        }
        
        stepData.textFields.forEach((field, index) => {
            const obj = this.textObjects.get(index);
            // Check if the object exists before calling set
            if (obj) {
                obj.set('visible', false);
            }
        });

        this.canvas.canvas.requestRenderAll();
    }
    prepareForPreview(){

        this.show();

    }

}